// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/chats.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatsPage.test.tsx
import {
  MAX_ASK_PUNTT_CHAT_TITLE_LENGTH,
  type AskPunttChatUserSummary,
} from "@mg/schemas/src/christo/askPuntt";
import { ChatCircle, Plus, Trash } from "@phosphor-icons/react";
import { Button, Callout, Dialog, Flex, Text } from "@radix-ui/themes";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ListCardGridFooter,
  ListCardRowMembers,
  ListCardRowTimestamps,
} from "./ListCardGridFooter";
import {
  formatListCardAbsoluteDate,
  LIST_CARD_GRID_HEIGHT_CLASS,
  LIST_CARD_GRID_OPEN_BTN_CLASS,
  LIST_CARD_TITLE_RENAME_DELETE_RESERVE_CLASS,
} from "./listCardTimestamps";
import { ListItemDeleteButton } from "./ListItemDeleteButton";
import { ListItemRenameButton } from "./ListItemRenameButton";
import { UnreadDot } from "./UnreadDot";
import { useInlineRename } from "./useInlineRename";

import { LauncherOrgToolbar } from "../../routes/ask-puntt/components/LauncherOrgToolbar";
import { OrgLabelChips } from "../../routes/ask-puntt/components/OrgLabelChips";
import {
  type LauncherSearch,
  launcherSearch,
} from "../../utils/askPunttLauncherSearch";
import { useAppSelector } from "../../utils/hooks";
import { projectMemberLabel } from "../../utils/projectListMembers";
import {
  ASK_PUNTT_UNREAD_LIST_POLL_MS,
  useAskPunttAllChats,
  useDeleteAskPunttChat,
  useRenameAskPunttChat,
} from "../../utils/queries/askPuntt";
import { interactiveSurfaceClasses } from "../InteractiveSurface";
import { PageHeader } from "../PageHeader";
import { ToolbarViewToggle } from "../toolbar/ToolbarViewToggle";
import { TOOLBAR_CONTROL_SIZE } from "../toolbarControls";

const CHAT_CARD_HOVER_SHELL =
  "relative w-full rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-puntt-accent-7 hover:shadow-md";
const CHAT_LIST_ROW_HOVER_SHELL =
  "relative w-full rounded-lg border border-puntt-neutral-gray-5 bg-base-white transition-all hover:border-puntt-accent-7 hover:shadow-sm";
const CHAT_CARD_OPEN_BTN = `${LIST_CARD_GRID_OPEN_BTN_CLASS} ${LIST_CARD_GRID_HEIGHT_CLASS}`;
const CHAT_LIST_ROW_OPEN_BTN =
  "flex w-full cursor-pointer items-center gap-4 rounded-[inherit] border-0 bg-transparent p-4 text-left shadow-none";

const VIEW_STORAGE_KEY = "askpuntt-chats-view";

type ChatsView = "grid" | "list";

export type ChatListItem = {
  _id: string;
  title: string;
  userId?: string;
  ownerName?: string;
  role?: "owner" | "shared";
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
  createdAt?: string;
  updatedAt?: string;
  hasUnread?: boolean;
  members?: AskPunttChatUserSummary[];
  tags?: string[];
};

function getStoredView(): ChatsView {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // ignore
  }
  return "list";
}

export function formatChatDate(iso: string | undefined): string {
  return formatListCardAbsoluteDate(iso);
}

/** Match the list API: title OR any tag contains the query (case-insensitive). */
export function chatMatchesLauncherQuery(
  chat: { title?: string | null; tags?: string[] | null },
  q: string,
): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  if ((chat.title ?? "").toLowerCase().includes(needle)) return true;
  return (chat.tags ?? []).some((tag) => tag.toLowerCase().includes(needle));
}

function chatSubtitle(
  chat: ChatListItem,
  currentUserId: string | undefined,
): string | null {
  if (chat.userId !== currentUserId && chat.ownerName) {
    return `Owned by ${chat.ownerName}`;
  }
  if (chat.role === "shared") {
    return "Shared with you";
  }
  if (chat.projectId && chat.projectChatKind === "personal") {
    return "Project chat";
  }
  return null;
}

function canModifyChat(
  chat: ChatListItem,
  currentUserId: string | undefined,
): boolean {
  // Only the chat owner may delete. Treat unresolved auth as not-owner
  // so we never flash controls on someone else's chat.
  return (
    currentUserId != null &&
    chat.userId != null &&
    chat.userId === currentUserId
  );
}

function ChatRenameInput({
  chatId,
  draft,
  setDraft,
  commit,
  cancel,
}: {
  chatId: string;
  draft: string;
  setDraft: (next: string) => void;
  commit: () => void;
  cancel: () => void;
}) {
  const { t } = useTranslation("ui");
  return (
    <input
      autoFocus
      value={draft}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          cancel();
        }
      }}
      maxLength={MAX_ASK_PUNTT_CHAT_TITLE_LENGTH}
      aria-label={t("chatTitle")}
      data-testid={`chat-row-rename-input-${chatId}`}
      className="w-full min-w-0 rounded border border-puntt-accent-9 bg-base-white px-1.5 py-0.5 text-base font-bold text-puntt-neutral-gray-12 outline-none focus:ring-1 focus:ring-puntt-accent-8"
    />
  );
}

function ChatOwnerActions({
  chat,
  isRenaming,
  isPending,
  onStartRename,
  onRequestDelete,
}: {
  chat: ChatListItem;
  isRenaming: boolean;
  isPending: boolean;
  onStartRename: () => void;
  onRequestDelete: (chatId: string) => void;
}) {
  if (isRenaming) return null;
  return (
    <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
      <ListItemRenameButton
        ariaLabel={`Rename chat ${chat.title}`}
        data-testid={`chat-row-rename-${chat._id}`}
        isPending={isPending}
        onClick={onStartRename}
      />
      <ListItemDeleteButton
        ariaLabel={`Delete chat ${chat.title}`}
        data-testid={`chat-row-delete-${chat._id}`}
        onClick={() => onRequestDelete(chat._id)}
      />
    </div>
  );
}

function ChatCard({
  chat,
  currentUserId,
  onOpen,
  onRequestDelete,
}: {
  chat: ChatListItem;
  currentUserId: string | undefined;
  onOpen: (chat: ChatListItem) => void;
  onRequestDelete: (chatId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const subtitle = chatSubtitle(chat, currentUserId);
  const members = chat.members ?? [];
  const canModify = canModifyChat(chat, currentUserId);
  const renameMutation = useRenameAskPunttChat();
  const { isRenaming, draft, setDraft, startRename, commit, cancel } =
    useInlineRename({
      value: chat.title,
      onCommit: (next) =>
        renameMutation.mutate({ chatId: chat._id, title: next }),
    });

  return (
    <div
      className="group relative"
      data-testid={`chat-row-${chat._id}`}
      data-chat-id={chat._id}
    >
      {isRenaming ? (
        <div
          className={`${interactiveSurfaceClasses({
            interactive: false,
          })} ${LIST_CARD_GRID_HEIGHT_CLASS} flex w-full flex-col justify-between border-puntt-accent-7 p-5 pb-4 text-left`}
        >
          <div className="min-w-0">
            <ChatRenameInput
              chatId={chat._id}
              draft={draft}
              setDraft={setDraft}
              commit={commit}
              cancel={cancel}
            />
            {subtitle ? (
              <Text
                size="2"
                className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
                data-testid={`chat-row-${chat._id}-subtitle`}
              >
                {subtitle}
              </Text>
            ) : null}
            <OrgLabelChips
              testIdPrefix="chats"
              entityId={chat._id}
              tags={chat.tags}
            />
          </div>
          <ListCardGridFooter
            idPrefix="chat-row"
            entityId={chat._id}
            updatedAt={chat.updatedAt}
            createdAt={chat.createdAt}
            members={members}
            memberLabel={projectMemberLabel}
          />
        </div>
      ) : (
        <div
          className={CHAT_CARD_HOVER_SHELL}
          data-testid={`chat-row-surface-${chat._id}`}
        >
          <button
            type="button"
            onClick={() => onOpen(chat)}
            aria-label={t("chatsPage.openChat", { title: chat.title })}
            data-testid={`chat-row-open-${chat._id}`}
            className={CHAT_CARD_OPEN_BTN}
          >
            <div className="min-w-0">
              <Text
                as="div"
                size="4"
                weight="bold"
                className={`flex items-center gap-2 truncate text-puntt-neutral-gray-12${
                  canModify
                    ? ` ${LIST_CARD_TITLE_RENAME_DELETE_RESERVE_CLASS}`
                    : ""
                }`}
              >
                <span className="truncate">{chat.title}</span>
                {chat.hasUnread ? (
                  <UnreadDot
                    testId={`chat-row-unread-${chat._id}`}
                    ariaLabel={t("askPuntt.chats.unread")}
                  />
                ) : null}
              </Text>
              {subtitle ? (
                <Text
                  size="2"
                  className="mt-2 line-clamp-2 text-puntt-neutral-gray-10"
                  data-testid={`chat-row-${chat._id}-subtitle`}
                >
                  {subtitle}
                </Text>
              ) : null}
              <OrgLabelChips
                testIdPrefix="chats"
                entityId={chat._id}
                tags={chat.tags}
              />
            </div>
            <ListCardGridFooter
              idPrefix="chat-row"
              entityId={chat._id}
              updatedAt={chat.updatedAt}
              createdAt={chat.createdAt}
              members={members}
              memberLabel={projectMemberLabel}
              hideOnGroupHover={canModify}
            />
          </button>
          {canModify ? (
            <ChatOwnerActions
              chat={chat}
              isRenaming={isRenaming}
              isPending={renameMutation.isPending}
              onStartRename={startRename}
              onRequestDelete={onRequestDelete}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

function ChatListRow({
  chat,
  currentUserId,
  onOpen,
  onRequestDelete,
}: {
  chat: ChatListItem;
  currentUserId: string | undefined;
  onOpen: (chat: ChatListItem) => void;
  onRequestDelete: (chatId: string) => void;
}) {
  const { t } = useTranslation("ui");
  const subtitle = chatSubtitle(chat, currentUserId);
  const members = chat.members ?? [];
  const canModify = canModifyChat(chat, currentUserId);
  const renameMutation = useRenameAskPunttChat();
  const { isRenaming, draft, setDraft, startRename, commit, cancel } =
    useInlineRename({
      value: chat.title,
      onCommit: (next) =>
        renameMutation.mutate({ chatId: chat._id, title: next }),
    });

  return (
    <div
      className="group relative"
      data-testid={`chat-row-${chat._id}`}
      data-chat-id={chat._id}
    >
      {isRenaming ? (
        <div
          className={`${interactiveSurfaceClasses({
            interactive: false,
          })} flex w-full items-center gap-4 border-puntt-accent-7 p-4 pr-12 text-left`}
        >
          <div className="min-w-0 flex-1">
            <ChatRenameInput
              chatId={chat._id}
              draft={draft}
              setDraft={setDraft}
              commit={commit}
              cancel={cancel}
            />
            {subtitle ? (
              <Text
                size="1"
                className="mt-1 block truncate text-puntt-neutral-gray-11"
                data-testid={`chat-row-${chat._id}-subtitle`}
              >
                {subtitle}
              </Text>
            ) : null}
            <OrgLabelChips
              testIdPrefix="chats"
              entityId={chat._id}
              tags={chat.tags}
            />
          </div>
          <Flex align="center" gap="3" className="ml-auto shrink-0">
            <ListCardRowTimestamps
              idPrefix="chat-row"
              entityId={chat._id}
              updatedAt={chat.updatedAt}
              createdAt={chat.createdAt}
            />
            <ListCardRowMembers
              idPrefix="chat-row"
              entityId={chat._id}
              members={members}
              memberLabel={projectMemberLabel}
            />
          </Flex>
        </div>
      ) : (
        <div
          className={CHAT_LIST_ROW_HOVER_SHELL}
          data-testid={`chat-row-surface-${chat._id}`}
        >
          <button
            type="button"
            onClick={() => onOpen(chat)}
            aria-label={t("chatsPage.openChat", { title: chat.title })}
            data-testid={`chat-row-open-${chat._id}`}
            className={CHAT_LIST_ROW_OPEN_BTN}
          >
            <div className="min-w-0 flex-1">
              <Text
                size="3"
                weight="medium"
                className={`flex items-center gap-2 truncate text-puntt-neutral-gray-12${
                  canModify
                    ? ` ${LIST_CARD_TITLE_RENAME_DELETE_RESERVE_CLASS}`
                    : ""
                }`}
              >
                <span className="truncate">{chat.title}</span>
                {chat.hasUnread ? (
                  <UnreadDot
                    testId={`chat-row-unread-${chat._id}`}
                    ariaLabel={t("askPuntt.chats.unread")}
                  />
                ) : null}
              </Text>
              {subtitle ? (
                <Text
                  size="1"
                  className="block truncate text-puntt-neutral-gray-11"
                  data-testid={`chat-row-${chat._id}-subtitle`}
                >
                  {subtitle}
                </Text>
              ) : null}
              <OrgLabelChips
                testIdPrefix="chats"
                entityId={chat._id}
                tags={chat.tags}
              />
            </div>
            <Flex align="center" gap="3" className="ml-auto shrink-0">
              <ListCardRowTimestamps
                idPrefix="chat-row"
                entityId={chat._id}
                updatedAt={chat.updatedAt}
                createdAt={chat.createdAt}
              />
              <ListCardRowMembers
                idPrefix="chat-row"
                entityId={chat._id}
                members={members}
                memberLabel={projectMemberLabel}
              />
            </Flex>
          </button>
          {canModify ? (
            <ChatOwnerActions
              chat={chat}
              isRenaming={isRenaming}
              isPending={renameMutation.isPending}
              onStartRename={startRename}
              onRequestDelete={onRequestDelete}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export type AskPunttChatsViewProps = {
  listTo: "/ask-puntt/chats";
  onOpenChat: (chat: ChatListItem) => void;
  onNewChat: () => void;
};

export function AskPunttChatsView({
  listTo,
  onOpenChat,
  onNewChat,
}: AskPunttChatsViewProps) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const urlSearch = useSearch({ strict: false }) as LauncherSearch;
  const [search, setSearch] = useState(urlSearch.q ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch.q ?? "");
  const view: ChatsView = urlSearch.view ?? getStoredView();
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);
  const { data, isLoading, isError } = useAskPunttAllChats({
    scope: "all",
    search: debouncedSearch.trim() || undefined,
    participantIds: urlSearch.people,
    tags: urlSearch.tags,
    sortBy: urlSearch.sort ?? "updatedAt",
    refetchInterval: ASK_PUNTT_UNREAD_LIST_POLL_MS,
  });
  const deleteChatMutation = useDeleteAskPunttChat();
  const currentUserId = useAppSelector((state) => state.auth.value?.userID);
  const navigate = useNavigate();

  const syncLauncherSearch = useCallback(
    (patch: Partial<LauncherSearch>) => {
      navigate({
        to: listTo,
        search: launcherSearch({ ...urlSearch, ...patch }),
        replace: true,
      });
    },
    [listTo, navigate, urlSearch],
  );

  useEffect(() => {
    setSearch(urlSearch.q ?? "");
    setDebouncedSearch(urlSearch.q ?? "");
  }, [urlSearch.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  useEffect(() => {
    const q = debouncedSearch.trim() || undefined;
    if (q === urlSearch.q) return;
    syncLauncherSearch({ q });
  }, [debouncedSearch, urlSearch.q, syncLauncherSearch]);

  const chats = data?.chats ?? [];
  const total = data?.total ?? 0;
  const chatToDelete = chats.find((c) => c._id === chatIdToDelete);

  const sortedChats = useMemo(() => {
    const sort = urlSearch.sort ?? "updatedAt";
    const copy = [...chats];
    if (sort === "name") {
      copy.sort((a, b) =>
        (a.title ?? "").localeCompare(b.title ?? "", undefined, {
          sensitivity: "base",
        }),
      );
      return copy;
    }
    if (sort === "createdAt") {
      copy.sort(
        (a, b) =>
          new Date(b.createdAt ?? 0).getTime() -
          new Date(a.createdAt ?? 0).getTime(),
      );
      return copy;
    }
    copy.sort(
      (a, b) =>
        new Date(b.updatedAt ?? b.createdAt ?? 0).getTime() -
        new Date(a.updatedAt ?? a.createdAt ?? 0).getTime(),
    );
    return copy;
  }, [chats, urlSearch.sort]);

  const peopleOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const chat of chats) {
      const ownerId = chat.userId;
      if (ownerId) {
        byId.set(
          ownerId,
          chat.ownerName ?? (ownerId === currentUserId ? "You" : ownerId),
        );
      }
      for (const member of chat.members ?? []) {
        if (!member._id) continue;
        if (!byId.has(member._id)) {
          byId.set(member._id, projectMemberLabel(member));
        }
      }
    }
    return Array.from(byId.entries()).map(([_id, name]) => ({ _id, name }));
  }, [chats, currentUserId]);

  const knownTagSlugs = useMemo(() => {
    const tags: string[] = [];
    for (const chat of chats) {
      for (const tag of chat.tags ?? []) {
        if (tag.trim()) tags.push(tag);
      }
    }
    return tags;
  }, [chats]);

  const filteredChats = useMemo(() => {
    const q = debouncedSearch.trim();
    if (!q) return sortedChats;
    return sortedChats.filter((c) => chatMatchesLauncherQuery(c, q));
  }, [sortedChats, debouncedSearch]);

  const showAdminBanner =
    currentUserId !== undefined &&
    chats.some((c) => c.userId !== currentUserId);

  const isSearchPending = search.trim() !== debouncedSearch.trim();
  const hasFilters =
    debouncedSearch.trim().length > 0 ||
    (urlSearch.people?.length ?? 0) > 0 ||
    (urlSearch.tags?.length ?? 0) > 0;
  // why: isFetching is true on the 30s unread poll; gating the list on it
  // swapped real rows for a skeleton during background refetch.
  const listLoading = isLoading || isSearchPending;
  const showNoResults =
    !listLoading && !isError && hasFilters && filteredChats.length === 0;
  const showEmpty =
    !listLoading && !isError && !hasFilters && filteredChats.length === 0;

  const handleViewChange = (next: ChatsView) => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      // ignore
    }
    syncLauncherSearch({ view: next });
  };

  const handleDeleteChat = (chatId: string) => {
    deleteChatMutation.mutate(chatId, {
      onSuccess: () => setChatIdToDelete(null),
    });
  };

  return (
    <div
      className="ask-puntt-surface flex h-full flex-col overflow-y-auto"
      data-testid="ask-puntt-chats"
    >
      <PageHeader className="flex-nowrap justify-between gap-4">
        <Text size="5" weight="bold" className="shrink-0 text-puntt-accent-9">
          {t("chats")}
        </Text>
        <LauncherOrgToolbar
          testIdPrefix="chats"
          surface="chats"
          search={urlSearch}
          onSearchChange={syncLauncherSearch}
          searchValue={search}
          onSearchInputChange={setSearch}
          searchPlaceholder={t("searchChats")}
          peopleOptions={peopleOptions}
          knownTagSlugs={knownTagSlugs}
        >
          <ToolbarViewToggle
            value={view}
            onChange={handleViewChange}
            groupLabel={t("chatView")}
            data-testid="chats-view-toggle"
            testIds={{
              grid: "chats-view-grid",
              list: "chats-view-list",
            }}
          />

          <Button
            data-testid="chats-new-button"
            size={TOOLBAR_CONTROL_SIZE}
            onClick={onNewChat}
            className="cursor-pointer"
          >
            <Plus size={14} weight="bold" aria-hidden="true" />
            {t("newChat")}
          </Button>
        </LauncherOrgToolbar>
      </PageHeader>

      <div className="mx-auto w-full max-w-[960px] px-6 py-8">
        {showAdminBanner && (
          <Callout.Root
            color="blue"
            size="1"
            className="mb-4"
            data-testid="ask-puntt-chats-admin-banner"
          >
            <Callout.Text>{t("chatsPage.adminViewingAllChats")}</Callout.Text>
          </Callout.Root>
        )}

        {total > chats.length && (
          <Text
            size="1"
            className="mb-3 block text-puntt-neutral-gray-9"
            data-testid="ask-puntt-chats-total"
          >
            {t("chatsPage.showingCount", { shown: chats.length, total })}
          </Text>
        )}

        {listLoading ? (
          <div
            className={
              view === "grid" ? "grid grid-cols-2 gap-4" : "flex flex-col gap-2"
            }
            data-testid="chats-skeleton"
          >
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={
                  view === "grid"
                    ? "h-40 animate-pulse rounded-2xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                    : "h-20 animate-pulse rounded-lg border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2"
                }
              />
            ))}
          </div>
        ) : isError ? (
          <Callout.Root
            color="red"
            size="1"
            data-testid="ask-puntt-chats-error"
          >
            <Callout.Text>{t("failedLoadChats")}</Callout.Text>
          </Callout.Root>
        ) : showEmpty ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="4"
            className="mt-16"
            data-testid="chats-empty"
          >
            <ChatCircle
              size={40}
              weight="duotone"
              className="text-puntt-neutral-gray-8"
              aria-hidden="true"
            />
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("noChatsYet")}
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              {t("startConversation")}
            </Text>
            <Button
              data-testid="chats-empty-new-button"
              onClick={onNewChat}
              className="cursor-pointer"
            >
              <Plus size={14} weight="bold" aria-hidden="true" />
              {t("newChat")}
            </Button>
          </Flex>
        ) : showNoResults ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="3"
            className="mt-16"
            data-testid="chats-no-results"
          >
            <Text
              size="4"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("noChatsMatch")}
            </Text>
            <Text
              size="2"
              className="max-w-sm text-center text-puntt-neutral-gray-9"
            >
              {t("tryAdjustingSearch")}
            </Text>
            <Button
              variant="soft"
              color="gray"
              data-testid="chats-clear-filters"
              aria-label={t("clearSearch")}
              className="cursor-pointer"
              onClick={() => {
                setSearch("");
                syncLauncherSearch({
                  q: undefined,
                  people: undefined,
                  tags: undefined,
                });
              }}
            >
              {t("clearSearch")}
            </Button>
          </Flex>
        ) : view === "grid" ? (
          <div className="grid grid-cols-2 gap-4" data-testid="chats-grid">
            {filteredChats.map((chat) => (
              <ChatCard
                key={chat._id}
                chat={chat}
                currentUserId={currentUserId}
                onOpen={onOpenChat}
                onRequestDelete={setChatIdToDelete}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="chats-list">
            {filteredChats.map((chat) => (
              <ChatListRow
                key={chat._id}
                chat={chat}
                currentUserId={currentUserId}
                onOpen={onOpenChat}
                onRequestDelete={setChatIdToDelete}
              />
            ))}
          </div>
        )}
      </div>

      <Dialog.Root
        open={chatIdToDelete != null}
        onOpenChange={(open) => {
          if (!open) setChatIdToDelete(null);
        }}
      >
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>{t("deleteChatQ")}</Dialog.Title>
          <Dialog.Description>
            {/* One interpolated sentence, not a fragment plus a bare "?" JSX
                node: the question mark and word order both move in Arabic and
                German. Same keys AppSidebar renders for this dialog. */}
            {chatToDelete
              ? tCommon("nav.areYouSureDeleteChat", {
                  title: chatToDelete.title,
                })
              : tCommon("nav.areYouSureDeleteChatFallback")}
          </Dialog.Description>
          <Flex gap="2" mt="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              onClick={() => setChatIdToDelete(null)}
              data-testid="chats-delete-cancel"
            >
              {t("cancel")}
            </Button>
            <Button
              color="red"
              onClick={() =>
                chatIdToDelete != null && handleDeleteChat(chatIdToDelete)
              }
              disabled={deleteChatMutation.isPending}
              loading={deleteChatMutation.isPending}
              data-testid="chats-delete-confirm"
            >
              <Trash aria-hidden="true" />
              {t("delete")}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </div>
  );
}
